<template>
  <div
    v-on-clickaway="onBlur"
    :class="quicksearchWrapperDynamicClass"
    @blur.capture="onElementBlur"
  >
    <fade-transition>
      <div v-show="isDropdownVisible" class="backdrop" @click="onBlur" />
    </fade-transition>

    <a-icon
      :is-button="true"
      :icon="$options.consts.ICON.MAGNIFIER_3"
      :original="true"
      :no-hover="true"
      :width="16"
      :height="16"
      class="global-quicksearch__search-icon"
      @click="openSearchPage"
    />

    <a-visibility show :on="[$breakpoint.mobile]">
      <a-lazy-hydrate when-visible>
        <a-text-input
          v-model="searchPhrase"
          :focus-on-mount="focusOnMount"
          :max-length="$options.consts.QUICK_SEARCH_MAX_LENGTH_LIMITATION"
          :input-style="$options.consts.INPUT_STYLE.STYLE_5"
          :in-progress="inProgress"
          :hide-label="hideLabel"
          :placeholder="placeholder"
          class="global-quicksearch__input"
          :class="dynamicInputClass"
          ref="search"
          @initial-focus="setAsFocused"
          @focus="setAsFocused"
          @search="openSearchPage"
          @keyup.enter="openSearchPage"
          @keyup.esc="onCloseIconClick"
        />
      </a-lazy-hydrate>
    </a-visibility>

    <a-visibility hide :on="[$breakpoint.mobile]">
      <a-lazy-hydrate when-visible>
        <a-text-input
          v-model="searchPhrase"
          :focus-on-mount="focusOnMount"
          :max-length="$options.consts.QUICK_SEARCH_MAX_LENGTH_LIMITATION"
          :input-style="$options.consts.INPUT_STYLE.STYLE_5"
          :in-progress="inProgress"
          :hide-label="hideLabel"
          :placeholder="placeholder"
          class="global-quicksearch__input"
          :class="dynamicInputClass"
          ref="search"
          @initial-focus="setAsFocused"
          @focus="setAsFocused"
          @search="openSearchPage"
          @keyup.enter="openSearchPage"
          @keyup.esc="onCloseIconClick"
        />
      </a-lazy-hydrate>
    </a-visibility>

    <component
      :is="quicksearchResultsComponent"
      v-if="isDropdownVisible"
      :search-phrase="searchPhrase"
      :in-progress.sync="inProgress"
      :single-column="singleColumn"
      :source="source"
      :scrollable="scrollable"
      v-on="$listeners"
      @search="openSearchPage"
      @hook:mounted="isFocused = true"
    />

    <div
      v-if="isCloseIconVisible"
      class="global-quicksearch__close-icon-wrapper"
      @click="onCloseIconClick"
    >
      <span class="global-quicksearch__close-icon-text text-small">Clear</span>
      <a-icon
        :icon="$options.consts.ICON.CROSS"
        :original="true"
        :no-hover="true"
        :width="16"
        :height="16"
        class="global-quicksearch__close-icon"
      />
    </div>
  </div>
</template>

<script>
import { directive as onClickaway } from 'vue-clickaway'

import mixins from '@/utils/mixins'
import { PROP_TYPES, propValidator } from '@/utils/validators'
import { FM_DIR_ROUTE, ROUTE } from 'enums/routes'
import { INPUT_STYLE } from 'shared/ATextInput'
import { CLOSE_MOBILE_HEADER_MENU_EVENT } from '@/components/AHeader/AHeaderMobile/enums'
import {
  MIN_CHAR_COUNT_FOR_SEARCH,
  QUICKSEARCH_SOURCE
} from 'shared/AGlobalQuicksearch/enums'
import { MIN_CHAR_COUNT_FOR_TRACKING_SEARCH } from '@/utils/mixins/track-search'
import { mapMutations } from 'vuex'
import * as types from '@/store/mutation-types'
import { SEARCH_TRACKING_EVENT_TYPE } from 'enums/track-search'
import { hasParentWith } from '@fmpedia/helpers'
import { QUICK_SEARCH_MAX_LENGTH_LIMITATION } from 'enums/quicksearch'
import { ICON } from 'shared/AIcon/enums'
import AIcon from 'shared/AIcon/index.vue'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'

const QUICKSEARCH_SOURCES = Object.values(QUICKSEARCH_SOURCE)

const QUICKSEARCH_RESULTS_COMPONENT_BY_SOURCE = {
  [QUICKSEARCH_SOURCE.IL]: 'IlQuicksearchResults'
}

const WRAPPER_CLASS = 'global-quicksearch__wrapper'
const GLOBAL_SEARCH_DROPDOWN_OPEN_CLASS = 'global-search-dropdown-open'

export default {
  name: 'AGlobalQuicksearch',
  mixins: [mixins.urlGenerators, mixins.trackSearch, hydrationHelpers],
  components: {
    AIcon,
    IlQuicksearchResults: () => import('./IlQuicksearchResults')
  },
  directives: { onClickaway },
  props: {
    singleColumn: propValidator([PROP_TYPES.BOOLEAN], false, false),
    hideLabel: propValidator([PROP_TYPES.BOOLEAN], false, false),
    source: propValidator(
      [PROP_TYPES.STRING],
      false,
      QUICKSEARCH_SOURCE.IL,
      value => QUICKSEARCH_SOURCES.includes(value)
    ),
    placeholder: propValidator(
      [PROP_TYPES.STRING],
      false,
      'Search investingLive'
    ),
    withCloseIcon: propValidator([PROP_TYPES.BOOLEAN], false, false),
    focusOnMount: propValidator([PROP_TYPES.BOOLEAN], false, false),
    useTrackSearch: propValidator([PROP_TYPES.BOOLEAN], false, true),
    scrollable: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  consts: { INPUT_STYLE, QUICK_SEARCH_MAX_LENGTH_LIMITATION, ICON },
  data() {
    return {
      searchPhrase: '',
      isFocused: false,
      inProgress: false
    }
  },
  computed: {
    quicksearchResultsComponent() {
      return QUICKSEARCH_RESULTS_COMPONENT_BY_SOURCE[this.source]
    },
    isSearchPhraseValid() {
      return this.searchPhrase.length >= MIN_CHAR_COUNT_FOR_SEARCH
    },
    isDropdownVisible() {
      return (
        (this.isFocused || this.$refs?.search?.isFocused) &&
        this.isSearchPhraseValid
      )
    },
    isCloseIconVisible() {
      return this.withCloseIcon && !this.inProgress && this.isFocused
    },
    dynamicInputClass() {
      return {
        'no-search-icon': this.isDropdownVisible
      }
    },
    quicksearchWrapperDynamicClass() {
      return {
        [WRAPPER_CLASS]: true,
        focused: this.isFocused
      }
    },
    closeDropdownRecalculationTrigger() {
      return [this.$route.path, this.$_hydrationHelpers_isLayoutMobile].join('')
    }
  },
  watch: {
    closeDropdownRecalculationTrigger() {
      this.onCloseIconClick()
    },
    isDropdownVisible(newVal) {
      /**
       * We need this to dynamically hide mobile bottom menu when
       * the dropdown is open
       */
      if (newVal) {
        if (this.$_hydrationHelpers_isLayoutMobile) {
          this.$_hydrationHelpers_hideScroll()
        }

        this.$_rootContainer_addClassToRootContainer(
          GLOBAL_SEARCH_DROPDOWN_OPEN_CLASS
        )
      } else {
        this.$_hydrationHelpers_showScroll()
        this.$_rootContainer_removeClassFromRootContainer(
          GLOBAL_SEARCH_DROPDOWN_OPEN_CLASS
        )
      }
    },
    searchPhrase: function() {
      if (
        this.useTrackSearch &&
        this.searchPhrase?.length >= MIN_CHAR_COUNT_FOR_TRACKING_SEARCH
      ) {
        this.$_trackSearch_debouncedTrackSearch(
          this.searchPhrase,
          SEARCH_TRACKING_EVENT_TYPE.IL_GLOBAL_SEARCH
        )
      }
    }
  },
  methods: {
    ...mapMutations({
      setSkipSearchTrackOnSearchResultsPageStatus: `globalSearch/${types.SET_SKIP_SEARCH_TRACK_ON_SEARCH_RESULTS_PAGE_STATUS}`
    }),
    setAsFocused() {
      this.isFocused = true
    },
    openSearchPage() {
      if (!this.isSearchPhraseValid) return

      if (this.source === QUICKSEARCH_SOURCE.IL) {
        this.$router.push({
          path: ROUTE.SEARCH_RESULTS,
          query: {
            searchPhrase: this.searchPhrase
          }
        })

        this.setSkipSearchTrackOnSearchResultsPageStatus(true)

        if (this.$refs.search) {
          this.$refs.search.$refs.input.blur()
        }

        this.searchPhrase = ''
        this.onBlur()
        this.$bus.$emit(CLOSE_MOBILE_HEADER_MENU_EVENT)
      } else {
        const queryString = this.$helper.serializeQueryParams({
          searchPhrase: this.searchPhrase
        })
        const link = `${this.generateFmDirUrl(
          FM_DIR_ROUTE.SEARCH_RESULTS
        )}?${queryString}`

        window.location.replace(link)
      }
    },
    onCloseIconClick() {
      this.searchPhrase = ''
      this.onBlur()
    },
    onElementBlur(event) {
      const { relatedTarget } = event

      if (
        relatedTarget &&
        !hasParentWith(event.relatedTarget, {
          className: WRAPPER_CLASS
        })
      ) {
        this.onBlur()
      }
    },
    onBlur() {
      this.$nextTick(() => {
        this.isFocused = false
        this.$emit('blur')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.global-quicksearch__wrapper {
  position: relative;
  max-width: 800px;

  .global-quicksearch__search-icon {
    width: 16px;
    height: 16px;
    position: absolute;
    left: 16px;
    top: 13px;
    z-index: 1;
    cursor: pointer;
  }

  /deep/ .input__wrapper .input__placeholder {
    padding-left: 32px;
  }

  /deep/ .input__input {
    padding-right: 70px;

    @include subtitle5;
  }

  .global-quicksearch__input {
    .no-search-icon {
      /deep/ .input__icon-action {
        display: none;
      }
    }

    /deep/ .input__placeholder {
      font-weight: 700;
    }
  }
}

.global-quicksearch__close-icon-wrapper {
  position: absolute;
  right: 16px;
  top: 13px;
  display: flex;
  cursor: pointer;

  .global-quicksearch__close-icon-text {
    margin-right: 4px;
    color: $c--gray-light;
  }
}

.close-icon {
  position: static;

  &::before,
  &::after {
    background: $c--white;
  }
}

.backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba($c--black, 0.75);
}
</style>
